import React, { useCallback, useEffect, useState } from 'react';
import { Search, Download, X, Users } from 'lucide-react';
import api from '../lib/api';
import { formatCurrency } from '../mock';

const SCOPES = [['all', 'Todos'], ['active', 'Em andamento'], ['delivered', 'Entregues'], ['archived', 'Arquivados'], ['manual', 'Manuais']];
const FIN = ['Cash', 'Finance', 'Outside Finance', 'Lease'];

// Arquivo de negociações: busca por cliente/telefone/veículo + histórico completo de qualquer deal.
export const DealHistory = ({ onOpen, stages = {}, canOpen = true }) => {
  const [f, setF] = useState({ q: '', scope: 'all', date_from: '', date_to: '', salesperson: '', status: '', finance_type: '' });
  const [data, setData] = useState({ items: [], totals: {}, salespeople: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams(Object.entries(f).filter(([, v]) => v));
      const { data: d } = await api.get(`/deals-history?${p.toString()}`);
      setData(d);
    } catch { setData({ items: [], totals: {}, salespeople: [] }); }
    finally { setLoading(false); }
  }, [f]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const clear = () => setF({ q: '', scope: 'all', date_from: '', date_to: '', salesperson: '', status: '', finance_type: '' });
  const active = f.q || f.date_from || f.date_to || f.salesperson || f.status || f.finance_type || f.scope !== 'all';

  const exportCsv = () => {
    const head = ['Deal', 'Cliente', 'Telefone', 'E-mail', 'Veiculo', 'Stock', 'VIN', 'Vendedor', 'Pagamento', 'Etapa', 'Data', 'Entrega', 'Valor', 'Gross', 'Manual', 'Arquivado'];
    const rows = data.items.map((i) => [i.id, i.customer_name, i.phone, i.email, i.vehicle_desc, i.stock, i.vin,
      i.salesperson_name, i.finance_type, stages[i.status]?.label || i.status, i.sale_date, i.delivery_date,
      i.sale_price, i.gross, i.manual ? 'Sim' : '', i.archived ? 'Sim' : '']);
    const csv = [head, ...rows].map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8' }));
    a.download = `negociacoes-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div className="space-y-3" data-testid="fi-history">
      <div className="flex flex-wrap items-end gap-2 border-y border-border py-3">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground/60" />
          <input data-testid="dh-search" className="ent-input pl-8 w-[300px]" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })}
            placeholder="Cliente, telefone, e-mail, VIN, stock, deal, vendedor..." />
        </div>
        <div><p className="ent-label mb-1">De</p><input data-testid="dh-from" type="date" className="ent-input" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} /></div>
        <div><p className="ent-label mb-1">Até</p><input data-testid="dh-to" type="date" className="ent-input" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} /></div>
        <div><p className="ent-label mb-1">Vendedor</p>
          <select data-testid="dh-salesperson" className="ent-input" value={f.salesperson} onChange={(e) => setF({ ...f, salesperson: e.target.value })}>
            <option value="">Todos</option>
            {(data.salespeople || []).map((s) => <option key={s} value={s} className="bg-popover">{s}</option>)}
          </select>
        </div>
        <div><p className="ent-label mb-1">Etapa</p>
          <select data-testid="dh-status" className="ent-input" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
            <option value="">Todas</option>
            {Object.entries(stages).map(([k, m]) => <option key={k} value={k} className="bg-popover">{m.label}</option>)}
          </select>
        </div>
        <div><p className="ent-label mb-1">Pagamento</p>
          <select data-testid="dh-finance" className="ent-input" value={f.finance_type} onChange={(e) => setF({ ...f, finance_type: e.target.value })}>
            <option value="">Todos</option>
            {FIN.map((x) => <option key={x} value={x} className="bg-popover">{x}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-1.5">
          {SCOPES.map(([k, l]) => (
            <button key={k} data-testid={`dh-scope-${k}`} onClick={() => setF({ ...f, scope: k })}
              className={`ent-btn ent-btn-sm ${f.scope === k ? 'ent-btn-primary' : ''}`}>{l}</button>
          ))}
        </div>
        {active && <button data-testid="dh-clear" onClick={clear} className="ent-btn ent-btn-sm"><X className="w-3 h-3" /> Limpar</button>}
        <button data-testid="dh-export" onClick={exportCsv} disabled={!data.items.length} className="ent-btn ent-btn-sm ml-auto disabled:opacity-40"><Download className="w-3 h-3" /> Exportar CSV</button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-border border border-border rounded-sm overflow-hidden" data-testid="dh-kpis">
        <div className="bg-card px-3 py-2"><p className="ent-label">Negociações</p><p className="ent-value mt-1" data-testid="dh-count">{data.totals?.count ?? 0}</p></div>
        <div className="bg-card px-3 py-2"><p className="ent-label">Faturamento</p><p className="ent-value mt-1">{formatCurrency(data.totals?.revenue || 0)}</p></div>
        <div className="bg-card px-3 py-2"><p className="ent-label">Ticket médio</p><p className="ent-value mt-1">{formatCurrency(data.totals?.avg || 0)}</p></div>
        <div className="bg-card px-3 py-2"><p className="ent-label">Gross total</p><p className="ent-value mt-1 t-ok">{formatCurrency(data.totals?.gross || 0)}</p></div>
      </div>

      <div className="overflow-x-auto">
        <table className="cm-table" data-testid="dh-table">
          <thead><tr>
            <th style={{ width: 86 }}>Data</th><th style={{ width: 78 }}>Deal</th><th>Cliente</th><th style={{ width: 130 }}>Contato</th>
            <th>Veículo</th><th style={{ width: 140 }}>Vendedor</th><th style={{ width: 92 }}>Pagamento</th>
            <th style={{ width: 150 }}>Etapa</th><th className="cm-num" style={{ width: 104 }}>Valor</th>
          </tr></thead>
          <tbody>
            {data.items.map((i) => (
              <tr key={i.id} data-testid={`dh-row-${i.id}`} onClick={() => canOpen && onOpen(i.id)} className={canOpen ? 'cursor-pointer' : ''}>
                <td className="num cm-muted">{i.sale_date || '—'}</td>
                <td className="num">{i.id}</td>
                <td className="max-w-[190px]">
                  <span className="truncate block font-medium">{i.customer_name}</span>
                  {i.customer_deals > 1 && (
                    <button data-testid={`dh-cust-${i.id}`} onClick={(e) => { e.stopPropagation(); setF({ ...f, q: i.customer_name, scope: 'all' }); }}
                      className="text-[10px] t-warn inline-flex items-center gap-1 hover:underline"><Users className="w-2.5 h-2.5" /> {i.customer_deals} negociações</button>
                  )}
                </td>
                <td className="cm-muted text-[11px] max-w-[130px] truncate">{i.phone || i.email || '—'}</td>
                <td className="max-w-[230px]"><span className="truncate block">{i.vehicle_desc}</span><span className="cm-muted text-[10px] mono">#{i.stock} {i.vin ? `· ${i.vin.slice(-6)}` : ''}</span></td>
                <td className="max-w-[140px] truncate">{i.salesperson_name || '—'}</td>
                <td className="cm-muted text-[11px]">{i.finance_type || '—'}</td>
                <td>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-semibold whitespace-nowrap ${stages[i.status]?.cls || ''}`}>{stages[i.status]?.label || i.status}</span>
                  {i.canceled && <span className="ent-tag t-bad ml-1">CANCELADA</span>}
                  {i.archived && <span className="ent-tag ml-1">ARQ</span>}
                  {i.manual && <span className="ent-tag t-warn ml-1">MANUAL</span>}
                </td>
                <td className="cm-money">{formatCurrency(i.sale_price)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && !data.items.length && <p className="ent-empty" data-testid="dh-empty">Nenhuma negociação encontrada com esses filtros.</p>}
        {loading && <p className="ent-empty">Buscando negociações...</p>}
      </div>
      <p className="text-[11px] t-label">{canOpen ? 'Clique em qualquer linha para abrir a ficha completa: timeline, documentos, garantia, título, registro, notas e laudo da inspeção.' : 'Visualização somente leitura — a ficha completa do deal é restrita à gerência e ao F&I.'}</p>
    </div>
  );
};

export default DealHistory;
